/**
 * Мессенджер платформы — единственная дверь к переписке из кабинета.
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ СЛОЙ. Экран должен думать про сообщения, а не про то, куда
 * ходить и что делать с отказом. Здесь собрано всё, что знает про API:
 * заголовки, курсоры, разбор ответа. Разложи это по компонентам — и правило
 * «ошибка не роняет экран» придётся повторить в каждом.
 *
 * ПОЛНАЯ ДОКУМЕНТАЦИЯ: mentorme-backend-v2/docs/messenger.md
 */
import axios from 'axios';

/** Заголовки: токен клиента + JSON. */
function headers() {
  return {
    // Без Accept неавторизованный запрос отвечает РЕДИРЕКТОМ на страницу
    // входа, а не понятной ошибкой: axios пойдёт по нему и вернёт HTML.
    Accept: 'application/json',
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  };
}

/**
 * Единая форма возврата: `{ ok, data, error, status }`.
 *
 * ОШИБКИ НЕ БРОСАЮТСЯ. Недоступность сервера не должна ронять экран: лента
 * останется прежней, а человеку скажут, что связь потеряна. Бросай мы
 * исключение — каждый вызов пришлось бы оборачивать try/catch, и первый же
 * забытый уронил бы кабинет.
 *
 * ЗАПАСНОЙ ТЕКСТ НАЗЫВАЕТ ДЕЙСТВИЕ, а не всегда «загрузку». Провал ОТПРАВКИ
 * сообщался словами «Не удалось загрузить переписку» — человек читал это как
 * «лента не открылась», хотя лента перед ним, а не ушло его сообщение.
 */
async function call(run, fallback = 'Не удалось загрузить переписку') {
  try {
    const res = await run();
    return { ok: true, data: res.data?.data ?? res.data, error: null, status: res.status };
  } catch (e) {
    const status = e.response?.status ?? 0;

    // Конверт ошибок у платформы НЕ единый: часть ручек отдаёт
    // `{error:{message}}`, часть — `{message}`, часть — пустое тело.
    // Ветвимся по коду, текст берём какой есть.
    const error =
      e.response?.data?.error?.message ||
      e.response?.data?.message ||
      (status === 0 ? 'Нет связи с сервером' : fallback);

    return { ok: false, data: null, error, status };
  }
}

export default {
  /** Мой разговор с командой. Заводит чат, если его ещё не было. */
  myChat() {
    return call(() => axios.get('/messenger/chats/mine', { headers: headers() }));
  },

  /** Состояние чата без ленты — для значка непрочитанного. */
  chat(uuid) {
    return call(() => axios.get(`/messenger/chats/${uuid}`, { headers: headers() }));
  },

  /**
   * Лента.
   *
   * `afterSeq` — опрос («что нового»), `beforeSeq` — история («что старше»).
   * Курсоры взаимно исключают друг друга: вместе они означали бы окно,
   * которого экран не просит.
   */
  messages(uuid, { afterSeq = null, beforeSeq = null, limit = 50 } = {}) {
    const params = { limit };
    if (afterSeq !== null) params.after_seq = afterSeq;
    else if (beforeSeq !== null) params.before_seq = beforeSeq;

    return call(() => axios.get(`/messenger/chats/${uuid}/messages`, { params, headers: headers() }));
  },

  /**
   * Написать.
   *
   * `clientMessageUuid` придумывает ОТПРАВИТЕЛЬ и держит его, пока жив
   * черновик: связь рвётся ровно тогда, когда ответ уже ушёл, но не дошёл.
   * Повтор с тем же ключом вернёт то же сообщение, а не второе такое же.
   */
  send(uuid, { clientMessageUuid, text = null, attachmentUuids = [], replyTo = null, answers = null, actionId = null, value = null }) {
    const body = { client_message_uuid: clientMessageUuid };

    if (text) body.text = text;
    if (attachmentUuids.length) body.attachment_uuids = attachmentUuids;
    if (replyTo) body.reply_to_message_uuid = replyTo;
    if (answers) body.answers_message_uuid = answers;
    if (actionId) body.action_id = actionId;
    if (value !== null) body.value = value;

    // ОТВЕТ ОТПРАВКИ ВЛОЖЕН В `message`, в отличие от остальных ручек, где
    // полезное лежит прямо в `data`. Разворачиваем здесь, чтобы экран получал
    // конверт сообщения, а не коробку с ним: иначе в ленту встаёт пустой пузырь
    // и это ничем себя не выдаёт.
    return call(() => axios.post(`/messenger/chats/${uuid}/messages`, body, { headers: headers() }), 'Сообщение не отправлено')
      .then((res) => (res.ok ? { ...res, data: res.data?.message ?? res.data } : res));
  },

  /**
   * Отметить прочитанным — РУБЕЖОМ, а не по сообщению.
   *
   * Сорок отметок на сорок прочитанных — сорок запросов там, где хватает
   * одного. Рубеж только растёт: перечитывание старого не возвращает
   * непрочитанное.
   */
  markRead(uuid, upToSeq) {
    return call(
      () => axios.post(`/messenger/chats/${uuid}/read`, { read_up_to_seq: upToSeq }, { headers: headers() }),
      'Отметка о прочтении не сохранилась',
    );
  },

  /**
   * Загрузить файл — ОДНИМ запросом.
   *
   * `onProgress` получает 0…100: большой файл по мобильному интернету едет
   * долго, и полоса — единственное, что отличает «грузится» от «зависло».
   */
  upload(uuid, file, { onProgress = null, voice = false, durationSec = null, width = null, height = null, waveform = null } = {}) {
    const form = new FormData();
    form.append('file', file);
    // ГОЛОСОВОЕ ОБЪЯВЛЯЕТ КЛИЕНТ. Без флага сервер сниффит webm как video/webm,
    // и голосовое приезжает «видео»: дефолтный плеер вместо волны.
    if (voice) form.append('voice', '1');
    if (durationSec !== null) form.append('duration_sec', Math.round(durationSec));
    if (width !== null) form.append('width', width);
    if (height !== null) form.append('height', height);
    if (waveform) waveform.slice(0, 512).forEach((v) => form.append('waveform[]', v));

    return call(() =>
      axios.post(`/messenger/chats/${uuid}/attachments`, form, {
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      }),
      'Файл не удалось отправить',
    );
  },
};
